import React, {useCallback, useContext, useEffect, useState} from 'react';

const PluginFormContext = React.createContext();

/**
 * A collection of form values where the key is the name of the field and the value is the value of the field.
 * @typedef {Record<string, any>} FormValues
 */

/**
 * A collection of form errors where the key is the name of the field and the value is the error.
 * @typedef {Record<keyof T, string | null | undefined>} FormErrors
 * @template {FormValues} T
 */

/**
 * A custom React Hook that provides a configuration context for plugin forms.
 * Thus, this hook will work only within a plugin form page - create or edit.
 *
 * @param {Object} param0 Form configuration.
 *
 * @param {<T extends FormValues>(values: T) => FormErrors<T> | Promise<FormErrors<T>} param0.handleValidate
 * A function that validates the form values and returns an errors object.
 * Can be a synchronous or asynchronous function.
 * [Official docs](https://formik.org/docs/api/formik#validate-values-values--formikerrorsvalues--promiseany).
 *
 * @param {Record<string, any>} param0.validationSchema
 * A Yup validation schema.
 * [Official docs](https://formik.org/docs/api/formik#validationschema-schema----schema).
 *
 * @param {(values: FormValues) => void | Promise} param0.handleSubmit
 * A function that handles the form submission. Can be a synchronous or asynchronous function.
 * If provided, this function will be invoked to handle the form submission.
 * Otherwise, the default form submission logic will be used.
 *
 * @param {React.DependencyList | undefined} deps
 * If present, the validation context will be updated when any of the dependencies changes.
 */
export const usePluginForm = ({handleSubmit, handleValidate, validationSchema}, deps) => {
    const context = useContext(PluginFormContext);
    if (!context) {
        throw new Error('useFormikValidation must be used within WithFormikValidation');
    }

    const {setFormikValidation, setHandleSubmit} = context;

    useEffect(() => {
        setFormikValidation({handleValidate, validationSchema});
        setHandleSubmit(handleSubmit);
    }, deps ?? []);
};

/**
 * An alias for `usePluginForm`.
 * @see {usePluginForm}
 * @deprecated Use `usePluginForm` instead. This alias will be removed with the next release.
 */
export const useFormikValidation = usePluginForm;

/**
 * A React Higher Order Component (HOC) that provides a configuration context for plugin forms.
 * Enables the usage of the `usePluginForm` hook.
 *
 * @param {Object} param0
 *
 * @param {(handler: (values: FormValues) => void | Promise) => void} param0.onHandleSubmitChange
 * A function that will be invoked when the form submission handler changes.
 * Provides the updated form submission handle function as parameter.
 */
export const WithPluginFormContext = ({onHandleSubmitChange, children}) => {
    const [formikValidation, setFormikValidation] = useState({handleValidate: null, validationSchema: null});

    const setHandleSubmit = useCallback(
        (handler) => {
            if (typeof onHandleSubmitChange === 'function') {
                onHandleSubmitChange(handler);
            }
        },
        [onHandleSubmitChange],
    );

    return (
        <PluginFormContext.Provider value={{setFormikValidation, setHandleSubmit}}>
            {children({
                validate: formikValidation.handleValidate,
                validationSchema: formikValidation.validationSchema,
            })}
        </PluginFormContext.Provider>
    );
};
